import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";

export default function ProfilePage() {
  const { user, logout, checkAuth } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [picture, setPicture] = useState(user?.picture || null);
  const [uploading, setUploading] = useState(false);
  const [delReqStatus, setDelReqStatus] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [reason, setReason] = useState("");
  const [submittingDel, setSubmittingDel] = useState(false);

  useEffect(() => {
    setPicture(user?.picture || null);
    checkDeletion();
  }, [user]);

  const checkDeletion = async () => {
    try {
      const { data } = await axios.get(`${API}/deletion-request/status`, { withCredentials: true });
      setDelReqStatus(data?.has_pending_request ? data.request : null);
    } catch (e) {
      // Non-critical — status check is allowed to fail silently in UI.
      logger.warn("checkDeletion failed:", e);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      return toast.show({ variant: "error", title: "Too large", description: "Image must be under 4MB." });
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await axios.put(`${API}/auth/profile-picture`, { picture: dataUrl }, { withCredentials: true });
      setPicture(data.picture);
      await checkAuth();
      toast.show({ variant: "success", title: "Profile picture updated" });
    } catch (err) {
      toast.show({ variant: "error", title: "Upload failed", description: err.response?.data?.detail || "Try again." });
    } finally {
      setUploading(false);
    }
  };

  const removePicture = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    setUploading(true);
    try {
      await axios.delete(`${API}/auth/profile-picture`, { withCredentials: true });
      setPicture(null);
      await checkAuth();
      toast.show({ variant: "success", title: "Removed" });
    } catch {
      toast.show({ variant: "error", title: "Failed to remove" });
    } finally {
      setUploading(false);
    }
  };

  const submitDeletion = async () => {
    setSubmittingDel(true);
    try {
      const { data } = await axios.post(`${API}/deletion-request`, { reason: reason || null }, { withCredentials: true });
      setDelReqStatus(data);
      setShowDelete(false);
      setReason("");
      toast.show({
        variant: "success",
        title: "Request submitted",
        description: "An admin will review your deletion request shortly.",
        duration: 6000,
      });
    } catch (err) {
      toast.show({ variant: "error", title: "Failed", description: err.response?.data?.detail || "Try again." });
    } finally {
      setSubmittingDel(false);
    }
  };

  const cancelDeletion = async () => {
    if (!window.confirm("Cancel your deletion request?")) return;
    try {
      await axios.delete(`${API}/deletion-request`, { withCredentials: true });
      setDelReqStatus(null);
      toast.show({ variant: "success", title: "Request cancelled" });
    } catch {
      toast.show({ variant: "error", title: "Failed" });
    }
  };

  return (
    <div className="px-5 pt-6 max-w-2xl mx-auto" data-testid="profile-page">
      <header className="mb-5">
        <h1 className="text-3xl font-extrabold tracking-tight">Profile</h1>
      </header>

      {/* Profile card */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 flex flex-col items-center text-center mb-5">
        <div className="relative">
          <button
            onClick={onPickFile}
            disabled={uploading}
            data-testid="change-avatar-btn"
            className="relative w-28 h-28 rounded-full overflow-hidden bg-slate-700 ring-2 ring-slate-600 flex items-center justify-center"
          >
            {uploading ? (
              <span className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full spin"></span>
            ) : picture ? (
              <img src={picture} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              <i className="fas fa-user text-4xl text-slate-400"></i>
            )}
          </button>
          <button
            onClick={onPickFile}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 w-9 h-9 bg-blue-600 rounded-full text-white flex items-center justify-center ring-4 ring-slate-800 btn-press"
            aria-label="Edit picture"
          >
            <i className="fas fa-camera text-sm"></i>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="avatar-file-input"
            onChange={onFileChange}
          />
        </div>
        <h2 className="text-2xl font-bold mt-4 text-white" data-testid="profile-name">{user?.name}</h2>
        <p className="text-slate-400 text-sm" data-testid="profile-email">{user?.email}</p>
        <div className="flex gap-2 mt-4">
          <button onClick={onPickFile} className="text-sm text-blue-400 hover:underline" data-testid="change-picture-link">
            <i className="fas fa-image mr-1.5"></i> Change picture
          </button>
          {picture && (
            <button onClick={removePicture} className="text-sm text-rose-400 hover:underline ml-2" data-testid="remove-picture-link">
              <i className="fas fa-xmark mr-1"></i> Remove
            </button>
          )}
        </div>
      </div>

      {/* Pending deletion notice */}
      {delReqStatus && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-5" data-testid="pending-deletion-notice">
          <div className="flex items-center gap-2 text-amber-300 font-semibold mb-1">
            <i className="fas fa-clock"></i> Deletion request pending
          </div>
          <p className="text-amber-200/80 text-sm mb-3">An admin is reviewing your request.</p>
          <button onClick={cancelDeletion} data-testid="cancel-deletion-btn" className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-sm font-medium px-4 py-2 rounded-lg">
            Cancel request
          </button>
        </div>
      )}

      {/* Info section */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl divide-y divide-slate-700 mb-5">
        <InfoRow icon="fa-envelope" label="Email" value={user?.email} />
        <InfoRow icon="fa-id-card" label="User ID" value={user?.user_id} mono />
      </div>

      <div className="text-center text-slate-500 text-sm mb-5">
        <i className="fas fa-book mr-1.5"></i> AusumLog · v1.0.0 PWA
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        className="w-full flex items-center justify-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 font-semibold py-3.5 rounded-2xl btn-press"
      >
        <i className="fas fa-right-from-bracket"></i> Sign out
      </button>

      {/* Delete account */}
      {!delReqStatus && (
        <button
          onClick={() => setShowDelete(true)}
          data-testid="request-deletion-btn"
          className="w-full mt-3 text-slate-500 hover:text-slate-300 text-sm py-2 flex items-center justify-center gap-2"
        >
          <i className="fas fa-trash"></i> Request account deletion
        </button>
      )}

      {/* Deletion modal */}
      {showDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 fade-in" onClick={() => setShowDelete(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="delete-account-modal" className="bg-slate-800 border border-slate-700 rounded-3xl p-6 w-full max-w-md">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-500/15 flex items-center justify-center text-rose-400 text-2xl mb-3">
                <i className="fas fa-triangle-exclamation"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Delete account</h3>
              <p className="text-slate-400 text-sm mt-1">
                Sent to an admin for review. Once approved, all your data is permanently deleted.
              </p>
            </div>
            <label className="block text-sm text-slate-400 mb-2">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tell us why…"
              rows={3}
              data-testid="deletion-reason-input"
              className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-slate-100 placeholder-slate-500 outline-none focus:border-rose-500 mb-4 resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDelete(false); setReason(""); }}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold py-3 rounded-xl btn-press"
              >
                Cancel
              </button>
              <button
                onClick={submitDeletion}
                disabled={submittingDel}
                data-testid="submit-deletion-btn"
                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold py-3 rounded-xl btn-press disabled:opacity-60 flex items-center justify-center"
              >
                {submittingDel ? (
                  <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full spin"></span>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-4 px-4 py-4">
      <div className="w-10 h-10 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
        <i className={`fas ${icon}`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        <div className={`text-slate-200 text-sm truncate ${mono ? "mono" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
